import { usePrivateWorkspace } from "@/api/workspaces";
import { MessageInput, MessageInputRef } from "@/components/MessageInput";
import { ReasoningEffortButton } from "@/components/MessageInput/components/ReasoningEffortButton";
import { MessageInputControlSet } from "@/constants/message-input";
import { useSessionContext } from "@/context/SessionContext";
import { ComposerAttachDropTarget } from "@/dnd/ComposerAttachDropTarget";
import { useRegisterOsFileDrop } from "@/dnd/OsFileDropLayer";
import { useRegisterComposerAttachTarget } from "@/dnd/TransferDndProvider";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useHarnessDraft } from "@/hooks/useHarnessDraft";
import { useMessageActions } from "@/hooks/useMessageActions";
import { useOpenChat } from "@/hooks/useOpenChat";
import { useReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import type { MetadataIcon, PermissionMode } from "@cohesive/api-client";
import {
  PERMISSION_MODES,
  PostMessageParams,
  ReferencedEntity,
} from "@cohesive/api-client";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { WorkspaceDropZone } from "../components/WorkspaceDropZone";
import { ComposerScopeChip } from "./ComposerScopeChip";

/**
 * Longer than ComposerShell's 150ms transition, short enough that the first
 * real hover can't realistically beat it.
 */
const MOUNT_SETTLE_MS = 250;

/**
 * False for the first moments after mount, then true forever.
 *
 * `autoFocus` reaches the editor through Lexical's plugin, which focuses in an
 * effect — i.e. *after* the first paint. So the composer paints its resting
 * border, then focus lands and ComposerShell's
 * `transition-[border-color,box-shadow]` animates it up to the focused border
 * and glow. Navigating between workspaces remounts the surface by design (the
 * `key` that resets the chat session), so that 150ms brighten replays on every
 * switch and reads as a flash.
 *
 * Nothing actually transitioned — the composer was born focused — so we hold
 * `transition-none` across the mount window and let it paint focused from the
 * first frame. Enabling the transition later never retroactively animates a
 * style already applied, so hover and blur behave normally from then on.
 *
 * A timer rather than rAF: React flushes passive effects (where Lexical
 * focuses) on its own schedule relative to paint, so a frame count isn't a
 * reliable "focus has landed" signal.
 */
function useTransitionSettled(): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(true), MOUNT_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, []);
  return settled;
}

export interface HomeComposerProps {
  /** Workspace the chat is started in. Undefined = the user's private workspace. */
  workspaceId?: string;
  workspaceName?: string;
  workspaceIcon?: MetadataIcon | null;
}

/**
 * Home composer: the shared MessageInput wired to start a chat. Sending creates
 * the flow session, posts the message, then lands on the full chat page
 * (`/chat/:flowSessionId`).
 *
 * File-drop targeting differs by scope. Unscoped, the composer claims OS drops
 * for the whole page (there is nowhere else for a file to go). Scoped, the
 * workspace page around it uploads dropped files into the workspace, so the
 * composer instead nests its own drop zone: drop on the input to attach,
 * anywhere else to upload. Registering the page-wide handler here would also
 * re-enable `OsFileDropLayer`, which *broadcasts* to every registered callback
 * — a drop would then attach AND upload.
 */
export function HomeComposer({
  workspaceId,
  workspaceName,
  workspaceIcon,
}: HomeComposerProps = {}) {
  const { openChat, canOpenFloating } = useOpenChat();
  const { sessionId, setCurrentSessionId } = useSessionContext();
  const [referencedEntities, setReferencedEntities] = useState<
    ReferencedEntity[]
  >([]);
  const messageInputRef = useRef<MessageInputRef>(null);
  const lg = useBreakpoint("lg");
  const scoped = !!workspaceId;
  const transitionSettled = useTransitionSettled();

  // Per-surface persistence (not the chat pages' per-session keys): the hero
  // composer has no session until send, and a floating draft can be mounted
  // at the same time — sharing its "draft" key would fight it.
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort(
    `composer_reasoning_effort_${workspaceId ?? "home"}`,
  );

  // Same per-surface persistence, for the same reason. Unlike the effort tier this one is spent
  // at session create rather than per message: the chat is stamped with it, movable only until
  // its first message runs.
  const [harnessDraft, setHarnessDraft] = useHarnessDraft(
    `composer_harness_${workspaceId ?? "home"}`,
  );

  // Deliberately NOT persisted, unlike the reasoning effort above: allow-all
  // is the dangerous mode, and a sticky setting would silently auto-accept
  // commands in chats the user never opted in for. Every new chat from this
  // composer starts at Auto. The pick needs no write of its own — it rides the
  // first message (`sendPermissionMode`) and stamps the minted session, and no
  // turn can run before that message.
  const [confirmationMode, setConfirmationMode] = useState<PermissionMode>(
    PERMISSION_MODES.AUTO,
  );

  // Unscoped, the backend files a new chat in the user's private workspace
  // (see the `workspaceId` prop note), so surface that scope the same way the
  // workspace pages surface theirs — "in My Workspace" — instead of leaving
  // the toolbar blank about where the chat lands.
  const privateWorkspace = usePrivateWorkspace();
  const scopeChip = scoped ? (
    workspaceName ? (
      <ComposerScopeChip
        name={workspaceName}
        icon={workspaceIcon}
        currentWorkspaceId={workspaceId}
      />
    ) : undefined
  ) : (
    <ComposerScopeChip
      name="My Workspace"
      icon={privateWorkspace?.metadata?.icon}
      /* Unscoped chats land in the private workspace, so that's the chip
         popover's checked row (its list has no Home entry), and selecting
         it keeps you on `/` — reaffirming the current target isn't a
         navigation. Any other workspace navigates to its page. */
      currentWorkspaceId={privateWorkspace?.id}
    />
  );

  const {
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
    onSendMessage,
    ensureFlowSession,
    isLoading,
  } = useMessageActions({
    workspaceId,
    harnessSelection: harnessDraft,
    onFlowSessionCreated: () => {
      analytics.trackNewChatCreated();
    },
    onMessageSent: ({ flowSession }) => {
      // A hero-composer send always starts a fresh thread — floating when the
      // surface is active, else the full chat page (which becomes a tab).
      openChat(flowSession.id, { mode: "replace", source: "hero-composer" });
      // Reset so the composer starts a fresh chat if the user comes back.
      setCurrentSessionId(undefined);
      // …and that fresh chat starts at Auto: an allow-all choice is scoped to
      // the chat it was made for, never inherited by the next one.
      setConfirmationMode(PERMISSION_MODES.AUTO);
    },
  });

  // Unscoped only: attach files dropped anywhere on the home page
  // (WorkspaceLayout provides the OsFileDropLayer this subscribes to).
  useRegisterOsFileDrop(scoped ? undefined : onAttachFile);

  // In-app dnd-kit drags (browser rows, rail explorer) land on the
  // `composer-attach` surface wrapping the input below: mint the pending
  // chat's session, copy the records in, enqueue them as attachments.
  useRegisterComposerAttachTarget(workspaceId ?? "home", {
    ensureSession: ensureFlowSession,
    onAttached: onAttachFileRecords,
  });

  const handleSend = async (params: PostMessageParams) => {
    const content = params.content?.trim();
    // Attachment-only sends are valid — MessageInput enables Send once
    // attachments finish uploading, and the ready file ids merge inside
    // useMessageActions (the send payload itself carries none). Only a
    // truly empty message is a no-op (backstop; MessageInput already
    // disables Send in that case).
    if (!content && uploadedFiles.length === 0) {
      return;
    }

    await onSendMessage({
      id: uuidv4(),
      content,
      file_record_ids: params.file_record_ids,
      referenced_entities: referencedEntities,
      // MessageInput stamps the selected effort onto its send params —
      // forward it so the hero pill actually reaches the API.
      reasoning_effort: params.reasoning_effort,
      // Only set once this composer has already minted its session and the user switched the
      // model on it — an unstamped draft's choice rides the create instead, so a message can
      // never carry a model minted for a harness the session didn't get.
      model: params.model,
      // A non-default draft pick; the first message stamps the minted session.
      permission_mode: params.permission_mode,
    });

    analytics.trackHomeMessageSent({
      content,
      fileRecordIds: params.file_record_ids ?? [],
    });
  };

  const composer = (
    /** In-app drag target wraps ONLY the input: drop on the composer to
     *  attach, anywhere else on the page to upload/move (the page's own
     *  targets). Distinct from the native-OS WorkspaceDropZone below. */
    <ComposerAttachDropTarget
      scopeKey={workspaceId ?? "home"}
      className="w-full rounded-xl"
    >
      {/* Elevation only — ComposerShell owns the framed surface (border +
       *  rust focus ring); a second frame would double-border the input. */}
      <div className="w-full rounded-xl shadow-elev-1">
        <MessageInput
          ref={messageInputRef}
          sendButtonDisabled={isLoading}
          onSend={handleSend}
          onAttachFile={onAttachFile}
          onAttachFileRecords={onAttachFileRecords}
          // A scoped composer's new chat lands in THIS workspace — its
          // The current workspace leads the picker (the private-workspace fallback would
          // misclassify same-workspace picks as cross-workspace).
          filesWorkspaceId={workspaceId}
          onRemoveFile={onRemoveFile}
          attachedFiles={uploadedFiles}
          onReferencedEntitiesChange={setReferencedEntities}
          autoFocus={lg}
          // When the send pops the floating window, its own autofocused input
          // should win — don't steal focus back a frame later.
          refocusOnSend={!canOpenFloating}
          location="main"
          controlSet={MessageInputControlSet.HOME_COMPOSER}
          harnessDraft={harnessDraft}
          onHarnessDraftChange={setHarnessDraft}
          // The composer stays session-less, but it mints one early on file attach — the
          // selector follows that session's stamp, writing through until its first message.
          harnessFlowSessionId={sessionId}
          // A draft mode; a non-default pick rides the first message and
          // stamps the session it mints.
          confirmationMode={confirmationMode}
          onConfirmationModeChange={setConfirmationMode}
          // Stamps `reasoning_effort` onto the send params; the pill itself
          // renders in the middle slot below (right of the scope chip), not
          // via the control set's built-in left-cluster placement.
          agentReasoningEffort={reasoningEffort}
          /* Sits immediately right of the skills button, left of the send
           cluster — so the composer's height never changes with scope. */
          middleToolbarSlot={
            <div className="flex min-w-0 items-center gap-1">
              {scopeChip}
              <ReasoningEffortButton
                effort={reasoningEffort}
                onChange={setReasoningEffort}
              />
            </div>
          }
          classNames={{
            container: "w-full",
            editor: "min-h-[72px]",
            // `transition-none` drops ComposerShell's transition for the mount
            // window — see useTransitionSettled.
            contentWrapper: cn(!transitionSettled && "transition-none"),
          }}
        />
      </div>
    </ComposerAttachDropTarget>
  );

  if (!scoped) return composer;

  return (
    <WorkspaceDropZone
      compact
      label="Drop files to attach to your message"
      className="rounded-xl"
      // Chat attachments have no folder concept — flatten any dropped tree.
      onDrop={({ files, tree }) =>
        onAttachFile([...files, ...(tree?.files ?? []).map((f) => f.file)])
      }
    >
      {composer}
    </WorkspaceDropZone>
  );
}
